import { useState, useEffect, useCallback, ReactNode, FC } from "react";
import ReactDOM from "react-dom";

export interface PortalProps {
  children: ReactNode;
}

interface PortalObject {
  render: FC<PortalProps>;
  remove: () => void;
}

const usePortal = (el: HTMLElement | null): FC<PortalProps> => {
  const [portal, setPortal] = useState<PortalObject | null>(null);

  const createPortal = useCallback((el: HTMLElement | null): PortalObject => {
    const Portal: FC<PortalProps> = ({ children }) => (el ? ReactDOM.createPortal(children, el) : null);
    const remove = () => {
      if (el) {
        ReactDOM.unmountComponentAtNode(el);
      }
    };
    return { render: Portal, remove };
  }, []);

  useEffect(() => {
    const newPortal = createPortal(el);
    if (portal) {
      portal.remove();
    }
    setPortal(newPortal);
    return () => newPortal.remove();
  }, [el, createPortal]);

  return portal ? portal.render : () => null;
};

export { usePortal };
